/**
 * DESIGNSPECS.JS | Micheals Infrastructure Advisory
 * COMPONENT: Infrastructure Architecture & Fiduciary Asset Stewardship
 * VERSION: 6.2.3 (Build 2026.06.24 - Consultative Finalization)
 * PRINCIPAL: Micheals Technical Governance
 */

import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  ChevronRight, ShieldCheck, 
  Wallet, LineChart 
} from 'lucide-react';
import SEO from '../components/SEO';
import ProjectLedger from '../components/ProjectLedger';
import ConsultingProtocol from '../components/ConsultingProtocol';

const DesignSpecs = () => {
  const navigate = useNavigate();

  const consultingProtocols = [
    {
      id: 1,
      title: "Strategic Infrastructure Architecture",
      icon: LineChart,
      financialImpact: "Eliminates systemic infrastructure over-builds and long-term liability exposure.",
      description: "We architect the logical backbone of your facility before capital is deployed. By establishing fixed technical blueprints, we neutralize vendor bias and ensure total project transparency.",
      image: "/assets/portfolio/smart_controller.webp",
      points: [
        { label: "Technical Oversight", detail: "Reduce CAPEX by 15% through precision architectural planning and redundant system elimination." },
        { label: "Lifecycle Asset Protection", detail: "Establish 15-year infrastructure horizons that outpace industry standard deprecation cycles." },
        { label: "Independent Procurement", detail: "Fixed technical specifications allow you to manage vendor implementation with absolute pricing control." }
      ]
    },
    {
      id: 2,
      title: "Performance Systems Governance",
      icon: ShieldCheck,
      financialImpact: "Reduces annual OpEx by 22% through predictive asset management and performance tuning.",
      description: "Proprietary forensic auditing identifying operational friction. We transition your facility from a reactive maintenance burden into a high-performance managed asset.",
      image: "/assets/portfolio/smart-light.webp",
      points: [
        { label: "Predictive Risk Mitigation", detail: "Advanced diagnostics detect latent system failures before they result in operational downtime." },
        { label: "Operational Load Optimization", detail: "Systemic balancing to maximize the performance and longevity of all capital-intensive assets." },
        { label: "Compliance & Risk Leverage", detail: "Certified architecture documentation used to negotiate reduced insurance liability premiums." }
      ]
    }
  ];

  return (
    <div className="bg-white min-h-screen selection:bg-amber-100 overflow-x-hidden">
      <SEO title="Infrastructure Governance | Micheals Infrastructure Advisory" description="Fiduciary asset stewardship and forensic infrastructure consulting." />
      
      <header className="relative pt-48 pb-32 px-6 bg-stone-950 flex flex-col items-center">
        <div className="max-w-6xl mx-auto text-center relative z-10">
          <div className="flex items-center justify-center gap-4 mb-10">
            <div className="h-px w-12 bg-amber-600" />
            <span className="text-[10px] font-black tracking-[0.6em] uppercase text-amber-500">
              Fiduciary Infrastructure Strategy
            </span>
            <div className="h-px w-12 bg-amber-600" />
          </div>
          <h1 className="text-7xl md:text-[9vw] font-serif italic text-white tracking-tighter leading-[0.85] mb-12">
            Capital <span className="not-italic font-light text-stone-600">Recaptured.</span>
          </h1>
          <p className="max-w-3xl mx-auto text-stone-400 text-xl md:text-2xl font-light leading-relaxed">
            Infrastructure is a capital asset, not a sunk cost. We provide the forensic oversight 
            required to eliminate inefficiency and maximize the long-term yield of your built environment.
          </p>
        </div>
      </header>

      <section className="px-6 max-w-7xl mx-auto space-y-20">
        {consultingProtocols.map((p) => (
          <ConsultingProtocol key={p.id} {...p} index={p.id} />
        ))}
      </section>

      <ProjectLedger />

      <section className="py-60 px-6 text-center bg-white">
        <motion.div 
          whileInView={{ opacity: 1, y: 0 }} 
          initial={{ opacity: 0, y: 40 }} 
          viewport={{ once: true }}
          className="max-w-4xl mx-auto"
        >
          <div className="mb-12 inline-block p-8 bg-stone-50">
            <Wallet className="text-amber-700" size={48} />
          </div>
          <h2 className="text-6xl md:text-7xl font-serif italic mb-16 text-stone-950 leading-none tracking-tighter">
            Stop funding waste. <br/> 
            <span className="not-italic font-light text-stone-400">Start governing performance.</span>
          </h2>
          <button 
            onClick={() => navigate('/book-consultation')}
            className="group relative bg-stone-950 text-white px-12 py-6 uppercase tracking-[0.4em] font-black text-[10px] hover:bg-amber-800 transition-all shadow-lg flex items-center gap-6 mx-auto"
          >
            <span>Engage Architectural Governance</span>
            <ChevronRight size={16} className="group-hover:translate-x-2 transition-transform" />
          </button>
        </motion.div>
      </section>
    </div>
  );
};

export default DesignSpecs;